<script>
	import { onMount } from "svelte";

	// Props
	export let placeholder = "Select...";
	export let disabled = false;
	export let error = false;
	export let errorText = "";
	export let showNavArrows = true;
	export let options = []; // all available options from parent
	export let value = null; // currently selected option
	export let onChange = () => {}; // when an item is selected
	export let onLoadMore = () => {}; // infinite scroll trigger

	let isOpen = false;
	let visibleOptions = [];
	let startIndex = 0;
	const loadCount = 10;

	onMount(() => {
		loadInitial();
	});

	function loadInitial() {
		visibleOptions = options.slice(0, loadCount);
		startIndex = loadCount;
	}

	// When options grow (e.g., after scroll load), update visibleOptions
	$: if (options.length > visibleOptions.length) {
		visibleOptions = options.slice(0, startIndex);
	}

	function toggleDropdown() {
		if (!disabled) isOpen = !isOpen;
	}

	function selectOption(option) {
		onChange(option); // emit selection
		isOpen = false;
	}

	function handleScroll(e) {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollTop + clientHeight >= scrollHeight - 10) {
			onLoadMore();
			startIndex += loadCount;
		}
	}

	function handleNext() {
		const idx = options.findIndex((r) => r.value === value?.value);
		if (idx < options.length - 1) onChange(options[idx + 1]);
	}

	function handlePrev() {
		const idx = options.findIndex((r) => r.value === value?.value);
		if (idx > 0) onChange(options[idx - 1]);
	}
</script>

<div class="select-wrapper">
	{#if showNavArrows}
		<button class="arrow-button" on:click={handlePrev} {disabled}
			>← Prev</button
		>
	{/if}

	<button class="dropdown" on:click={toggleDropdown}>
		{#if value}
			{value.label.split("–")[0].trim()}
		{:else}
			<span class="placeholder">{placeholder}</span>
		{/if}
		<img
			src={`/assets/icon-chevron-${isOpen ? "up" : "down"}.png`}
			width="18"
			height="18"
		/>
	</button>

	{#if isOpen}
		<div class="dropdown-list" on:scroll={handleScroll}>
			{#each visibleOptions as opt}
				<button class="option" on:click={() => selectOption(opt)}>
					{opt.label}
				</button>
			{/each}
		</div>
	{/if}

	{#if showNavArrows}
		<button class="arrow-button" on:click={handleNext} {disabled}
			>Next →</button
		>
	{/if}

	{#if error}
		<small class="error-text">{errorText}</small>
	{/if}
</div>

<style>
	.select-wrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		position: relative;
		width: 100%;
	}

	.dropdown {
		all: unset;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 16px;
		font-size: 24px;
		font-weight: 700;
		cursor: pointer;
		min-width: 25%;
		background-color: transparent;
		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-select: none;
	}

	.option {
		all: unset;
		box-sizing: border-box;
		display: block;
		width: 100%;
		padding: 10px 16px;
		cursor: pointer;
		white-space: nowrap;
		border-bottom: 1px solid #f0f0f0;
		text-align: left;
		font-size: 14px;
	}

	.option:hover {
		background-color: #f6f6f6;
	}

	.dropdown:active,
	.option:active {
		background-color: transparent;
	}

	.dropdown-list {
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		width: 75%;
		max-height: 200px;
		overflow-y: auto;
		background: white;
		border: 1px solid #ccc;
		border-radius: 8px;
		margin-top: 4px;
		z-index: 10;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
	}

	.placeholder {
		color: #aaa;
	}

	.arrow {
		margin-left: 8px;
		transition: transform 0.2s;
		display: inline-block;
		width: 16px;
	}

	.arrow-button {
		background: none;
		border: none;
		font-size: 14px;
		cursor: pointer;
		color: #333;
	}

	.arrow-button:disabled {
		color: #aaa;
		cursor: not-allowed;
	}

	.error-text {
		color: red;
		font-size: 0.85rem;
		margin-top: 4px;
	}
</style>
